import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { useCreateWorkspace, useUpdateWorkspace } from "@/hooks/use-workspaces";

import {
  defaultWorkspaceValues,
  workspaceSchema,
  type WorkspaceFormValues,
} from "./workspace-schema";
import { slugify } from "./workspace-utils";

import type { CreateWorkspaceReq, Workspace } from "@artisancode/api-types";

export function useWorkspaceForm(
  open: boolean,
  editing: Workspace | null,
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateWorkspace();
  const { mutate: update, isPending: isUpdating } = useUpdateWorkspace();

  const form = useForm<WorkspaceFormValues>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: defaultWorkspaceValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(
        editing
          ? {
              name: editing.name,
              slug: editing.slug,
              timezone: editing.timezone,
            }
          : defaultWorkspaceValues,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  function handleNameChange(value: string) {
    if (!editing && !form.getFieldState("slug").isDirty) {
      form.setValue("slug", slugify(value));
    }
  }

  function onSubmit(values: WorkspaceFormValues) {
    if (editing) {
      update(
        {
          id: editing.id,
          name: values.name,
          slug: values.slug,
          timezone: values.timezone,
        },
        {
          onSuccess: () => {
            toast.success("Workspace berhasil diperbarui.");
            onSuccess();
          },
        },
      );
    } else {
      const payload: CreateWorkspaceReq = {
        name: values.name,
        slug: values.slug,
        timezone: values.timezone,
        ...(values.copyMasterFromWorkspaceId &&
        values.copyMasterFromWorkspaceId !== "none"
          ? { copyMasterFromWorkspaceId: values.copyMasterFromWorkspaceId }
          : {}),
      };
      create(payload, {
        onSuccess: () => {
          toast.success("Workspace berhasil ditambahkan.");
          onSuccess();
        },
      });
    }
  }

  return {
    form,
    handleNameChange,
    isSubmitting: isCreating || isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
